import React from 'react';
import { Neo, Views } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import PreferredCommunicationMethodVM from './PreferredCommunicationMethodVM';
import { PreferredCommunicationMethod } from '../Models/Enums/PreferredCommunicationMethod';
import ContactDetailsView from './ContactDetailsView';

@observer
export default class PreferredCommunicationMethodView extends Views.ViewBase<PreferredCommunicationMethodVM> {

    constructor(props: unknown) {
        super("Communication Preferences", PreferredCommunicationMethodVM, props);
    }

    public get headerTitle() {
        return "My Profile";
    }

    private get radioItems() {
        return [
            { id: null as PreferredCommunicationMethod | null, text: "Use Org Default" },
            { id: PreferredCommunicationMethod.Email, text: "Email" },
            {
                id: PreferredCommunicationMethod.Sms,
                text: this.viewModel.isSmsBlocked ? "SMS (requires verified phone number)" : "SMS",
            },
        ];
    }

    private formatCommunicationMethod(method: PreferredCommunicationMethod | null): string {
        switch (method) {
            case PreferredCommunicationMethod.Email:
                return "Email";
            case PreferredCommunicationMethod.Sms:
                return "SMS";
            default:
                return "Not set";
        }
    }

    public render() {
        const vm = this.viewModel;
        const { preference } = vm;

        return (
            <div className="more-padding">
                <h2>Communication Preferences</h2>

                <p className="my-4">
                    Choose how you prefer to receive notifications. Your preference overrides the organisation default.
                </p>

                <div className="row">
                    <div className="col-xl-8 col-xxl-7">

                        <Neo.Card title="Organisation Default" icon="fa-building">
                            <Neo.GridLayout md={1}>
                                <Neo.FormGroupInline
                                    display={preference.meta.tenantDefaultCommunicationMethod}
                                    label="Default method"
                                    className="no-border"
                                    select={{
                                        items: [
                                            { id: PreferredCommunicationMethod.Email, text: "Email" },
                                            { id: PreferredCommunicationMethod.Sms, text: "SMS" },
                                        ], renderAsText: true, allowNulls: true, nullText: "Not set"
                                    }}
                                />
                                {preference.tenantDefaultLastUpdatedOn && (
                                    <Neo.FormGroupInline
                                        display={preference.meta.tenantDefaultLastUpdatedOn}
                                        label="Last updated"
                                        className="no-border"
                                    />
                                )}
                            </Neo.GridLayout>
                        </Neo.Card>

                        <Neo.Card title="Your Preference" icon="fa-user-cog" className="mt-3">
                            <Neo.FormGroup
                                bind={vm.meta.selectedMethod}
                                label="Communication method"
                                radioList={{ items: this.radioItems, inline: false, valueMember: "id", displayMember: "text" }}
                            />

                            {vm.isSmsBlocked && (
                                <Neo.Alert variant="info" icon="info-circle" className="mb-3">
                                    SMS is not available because your mobile number has not been verified.
                                    Visit <Neo.Link to={this.navigation.getPathForView(ContactDetailsView)}>Contact Details</Neo.Link> to update and verify your mobile number.
                                </Neo.Alert>
                            )}

                            <Neo.FormGroupInline
                                display={vm.meta.effectiveMethodDisplay}
                                label="Effective preference"
                                className="no-border mb-3 align-items-center display-white-bg"
                                md={3}
                            />

                            <Neo.Button
                                variant="primary"
                                className="btn-150"
                                icon="save"
                                disabled={vm.isSmsBlocked && vm.selectedMethod === PreferredCommunicationMethod.Sms}
                                onClick={() => vm.savePreference()}
                                task={vm.taskRunner}>
                                Save
                            </Neo.Button>
                        </Neo.Card>

                    </div>
                </div>
            </div>
        );
    }
}
